/**
 * CSS Scoping Service
 * 
 * Provides utilities for generating unique scope identifiers and scoping CSS
 * to prevent style conflicts between different email templates.
 */

export interface ScopedResult {
  scopeId: string;
  scopedCSS: string;
  wrappedHTML: string;
}

export class CSSScopingService {
  /**
   * Generate a unique scope identifier using timestamp and random string
   * Format: email-tpl-{timestamp}-{random}
   */
  generateScopeId(): string {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 11);
    return `email-tpl-${timestamp}-${random}`;
  }

  /**
   * Scope CSS selectors by prefixing them with the scope identifier
   * Skips :root and :host pseudo-classes
   * 
   * @param css - The CSS stylesheet to scope
   * @param scopeId - The unique scope identifier
   * @returns Scoped CSS with prefixed selectors
   */
  scopeCSS(css: string, scopeId: string): string {
    if (!css || !scopeId) return css;

    // Split CSS into rules
    const rules = this.parseCSS(css);
    const scopedRules = rules.map(rule => this.scopeRule(rule, scopeId));
    
    return scopedRules.join('\n');
  }

  /**
   * Parse CSS into individual rules
   * Simple parser that handles basic CSS syntax
   */
  private parseCSS(css: string): string[] {
    const rules: string[] = [];
    let currentRule = '';
    let braceCount = 0;
    let inComment = false;

    for (let i = 0; i < css.length; i++) {
      const char = css[i];
      const nextChar = css[i + 1];

      // Handle comments
      if (char === '/' && nextChar === '*') {
        inComment = true;
        currentRule += char;
        continue;
      }
      if (inComment && char === '*' && nextChar === '/') {
        inComment = false;
        currentRule += char;
        continue;
      }

      currentRule += char;

      if (!inComment) {
        if (char === '{') {
          braceCount++;
        } else if (char === '}') {
          braceCount--;
          
          if (braceCount === 0 && currentRule.trim()) {
            rules.push(currentRule.trim());
            currentRule = '';
          }
        }
      }
    }

    // Handle any remaining content
    if (currentRule.trim()) {
      rules.push(currentRule.trim());
    }

    return rules;
  }

  /**
   * Scope a single CSS rule
   */
  private scopeRule(rule: string, scopeId: string): string {
    // Extract selector and body
    const braceIndex = rule.indexOf('{');
    if (braceIndex === -1) return rule;

    const selector = rule.substring(0, braceIndex).trim();
    const body = rule.substring(braceIndex);

    // Split multiple selectors (comma-separated)
    const selectors = selector.split(',').map(s => s.trim());
    const scopedSelectors = selectors.map(sel => this.scopeSelector(sel, scopeId));

    return `${scopedSelectors.join(', ')} ${body}`;
  }

  /**
   * Scope a single selector
   * Skip :root, :host, and other pseudo-classes that shouldn't be scoped
   */
  private scopeSelector(selector: string, scopeId: string): string {
    // Don't scope :root or :host
    if (selector.startsWith(':root') || selector.startsWith(':host')) {
      return selector;
    }

    // Don't scope @-rules (like @media, @keyframes)
    if (selector.startsWith('@')) {
      return selector;
    }

    // Prefix the selector with the scope class
    return `.${scopeId} ${selector}`;
  }

  /**
   * Wrap HTML content with a scope wrapper div
   * 
   * @param html - The HTML content to wrap
   * @param scopeId - The unique scope identifier
   * @returns HTML wrapped with scope div
   */
  wrapHTMLWithScope(html: string, scopeId: string): string {
    if (!html || !scopeId) return html;
    
    return `<div class="${scopeId}">\n${html}\n</div>`;
  }

  /**
   * Extract inline styles from HTML elements
   * Returns a map of element identifiers to their inline styles
   */
  extractInlineStyles(html: string): Map<string, Record<string, string>> {
    const styleMap = new Map<string, Record<string, string>>();
    
    // Create a temporary DOM element to parse HTML
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    
    // Find all elements with inline styles
    const elementsWithStyles = doc.querySelectorAll('[style]');
    
    elementsWithStyles.forEach((element, index) => {
      const styleAttr = element.getAttribute('style');
      if (styleAttr) {
        const styles = this.parseInlineStyles(styleAttr);
        // Use element tag + index as identifier
        const identifier = `${element.tagName.toLowerCase()}-${index}`;
        styleMap.set(identifier, styles);
      }
    });
    
    return styleMap;
  }

  /**
   * Parse inline style attribute into an object
   */
  private parseInlineStyles(styleAttr: string): Record<string, string> {
    const styles: Record<string, string> = {};
    
    const declarations = styleAttr.split(';').filter(d => d.trim());
    
    declarations.forEach(declaration => {
      const colonIndex = declaration.indexOf(':');
      if (colonIndex > -1) {
        const property = declaration.substring(0, colonIndex).trim();
        const value = declaration.substring(colonIndex + 1).trim();
        styles[property] = value;
      }
    });
    
    return styles;
  }

  /**
   * Merge two style objects, with override taking precedence
   */
  mergeStyles(
    base: Record<string, string>,
    override: Record<string, string>
  ): Record<string, string> {
    return { ...base, ...override };
  }

  /**
   * Complete scoping workflow: generate scope ID, scope CSS, and wrap HTML
   * 
   * @param html - The HTML content
   * @param css - The CSS stylesheet
   * @returns Object containing scopeId, scopedCSS, and wrappedHTML
   */
  scopeTemplate(html: string, css: string = ''): ScopedResult {
    const scopeId = this.generateScopeId();
    const scopedCSS = css ? this.scopeCSS(css, scopeId) : '';
    const wrappedHTML = this.wrapHTMLWithScope(html, scopeId);

    return {
      scopeId,
      scopedCSS,
      wrappedHTML
    };
  }
}

// Export singleton instance
export const cssScopingService = new CSSScopingService();
